import { useState, useEffect } from 'react';
import { ShieldCheckIcon, ExclamationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import permissionService from '../../../services/api/permissionService';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * EmployeePermissionsTab - Manage employee role and permissions
 *
 * Enterprise-grade RBAC with custom permissions support:
 * - Predefined roles (Admin, Manager, Employee)
 * - Custom per-employee permission sets
 * - Self-edit protection
 * - Owner protection
 * - Full audit logging
 */
function EmployeePermissionsTab({ employee, onEmployeeUpdated, onClose, onUnsavedChanges }) {
  const { userId } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Data from API
  const [groupedPermissions, setGroupedPermissions] = useState({});
  const [currentPermissions, setCurrentPermissions] = useState([]);
  const [currentRoles, setCurrentRoles] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [hasPciAccess, setHasPciAccess] = useState(false);

  // Form state
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [isSelf, setIsSelf] = useState(false);

  // Category display configuration
  const categoryConfig = {
    employees: { label: 'Employee Management', order: 1 },
    scheduling: { label: 'Scheduling & Calendar', order: 2 },
    time_clock: { label: 'Time Clock', order: 3 },
    pto: { label: 'Paid Time Off', order: 4 },
    invoicing: { label: 'Invoicing', order: 5 },
    payments: { label: 'Payments', order: 6 },
    credit_memos: { label: 'Credit Memos', order: 7 },
    customers: { label: 'Customers', order: 8 },
    products: { label: 'Products', order: 9 },
    categories: { label: 'Product Categories', order: 10 },
    vendors: { label: 'Vendors', order: 11 },
    transactions: { label: 'Transactions', order: 12 },
    reports: { label: 'Reports', order: 13 },
    outreach: { label: 'Outreach', order: 14 },
    company: { label: 'Company', order: 15 },
    cms: { label: 'Website', order: 16 },
    settings: { label: 'Settings', order: 17 },
    teams: { label: 'Teams', order: 18 }
  };

  // Check if editing self
  useEffect(() => {
    if (employee && userId) {
      // Compare user IDs - employee.userId should match current user's ID
      const isSelfEdit = employee.userId === userId || employee.wristbandUserId === userId;
      setIsSelf(isSelfEdit);
    }
  }, [employee, userId]);

  // Fetch permissions data on mount
  useEffect(() => {
    const fetchData = async () => {
      if (!employee?.id) {
        setError('Employee ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');

        // Fetch system permissions, employee permissions, and company roles in parallel
        const [systemPermsResponse, employeePermsResponse, companyRolesResponse] = await Promise.all([
          permissionService.getSystemPermissions(),
          permissionService.getEmployeePermissions(employee.id),
          permissionService.getCompanyRoles()
        ]);

        // Set system permissions
        setGroupedPermissions(systemPermsResponse.groupedPermissions || {});

        // Set employee-specific data
        setCurrentRoles(employeePermsResponse.roles || []);
        setCurrentPermissions(employeePermsResponse.permissions || []);
        setIsOwner(employeePermsResponse.isOwner || false);
        setHasPciAccess(employeePermsResponse.hasPciAccess || false);

        // Set available company roles
        setAvailableRoles(companyRolesResponse.roles || []);

        // Set current role selection or custom mode
        if (employeePermsResponse.roles && employeePermsResponse.roles.length > 0) {
          const currentRole = employeePermsResponse.roles[0];
          if (currentRole.roleType === 'custom') {
            setIsCustomMode(true);
            setSelectedRoleId('');
            // Pre-select current permissions
            const currentPermissionCodes = employeePermsResponse.permissions.map(p => p.code);
            setSelectedPermissions(currentPermissionCodes);
          } else {
            setSelectedRoleId(currentRole.id);
            setIsCustomMode(false);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch permission data:', err);
        setError(err.response?.data?.error || err.message || 'Failed to load permission data');
        setLoading(false);
      }
    };

    fetchData();
  }, [employee?.id]);

  // Track unsaved changes
  useEffect(() => {
    if (!loading) {
      const hasChanges = isCustomMode
        ? selectedPermissions.length > 0
        : selectedRoleId && selectedRoleId !== currentRoles[0]?.id;
      onUnsavedChanges(hasChanges);
    }
  }, [selectedRoleId, selectedPermissions, isCustomMode, currentRoles, loading, onUnsavedChanges]);

  // Handle role change
  const handleRoleChange = (e) => {
    const value = e.target.value;

    if (value === 'custom') {
      // Switch to custom mode
      setIsCustomMode(true);
      setSelectedRoleId('');

      // Pre-select current permissions if employee already has custom role
      if (currentRoles.length > 0 && currentRoles[0].roleType === 'custom') {
        const currentPermissionCodes = currentPermissions.map(p => p.code);
        setSelectedPermissions(currentPermissionCodes);
      } else {
        // Start with empty selection
        setSelectedPermissions([]);
      }
    } else {
      // Switch to predefined role mode
      setIsCustomMode(false);
      setSelectedRoleId(value);
      setSelectedPermissions([]);
    }

    setError('');
  };

  // Handle individual permission toggle
  const handlePermissionToggle = (permissionCode) => {
    setSelectedPermissions(prev => {
      if (prev.includes(permissionCode)) {
        return prev.filter(code => code !== permissionCode);
      } else {
        return [...prev, permissionCode];
      }
    });
  };

  // Handle category "select all"
  const handleCategorySelectAll = (category) => {
    const categoryPermissions = groupedPermissions[category] || [];
    const categoryCodes = categoryPermissions.map(p => p.code);

    // Check if all are selected
    const allSelected = categoryCodes.every(code => selectedPermissions.includes(code));

    if (allSelected) {
      // Deselect all in category
      setSelectedPermissions(prev => prev.filter(code => !categoryCodes.includes(code)));
    } else {
      // Select all in category
      setSelectedPermissions(prev => {
        const newSet = new Set([...prev, ...categoryCodes]);
        return Array.from(newSet);
      });
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (isCustomMode) {
      if (selectedPermissions.length === 0) {
        setError('Please select at least one permission for the custom role');
        return;
      }
    } else {
      if (!selectedRoleId) {
        setError('Please select a role');
        return;
      }
    }

    if (isOwner) {
      setError('Cannot modify permissions for company owner');
      return;
    }

    if (isSelf) {
      setError('You cannot edit your own permissions');
      return;
    }

    try {
      setSaving(true);
      setError('');

      // Build request payload
      const payload = isCustomMode
        ? { customPermissions: selectedPermissions }
        : { roleId: selectedRoleId };

      await permissionService.updateEmployeePermissions(employee.id, payload);

      // Don't update local state - modal is closing
      // Parent will handle data refresh after close

      // Notify parent component (will refresh and close modal gracefully)
      if (onEmployeeUpdated) {
        onEmployeeUpdated();
      }

    } catch (err) {
      console.error('Failed to update permissions:', err);
      setError(err.response?.data?.error || err.message || 'Failed to update permissions');
      setSaving(false);
    }
    // Don't reset saving state on success - let modal close with button still showing "Saving..."
  };

  // Render loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading permissions...</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      <div className="flex-1 px-6 py-6 space-y-8 overflow-y-auto">

        {/* Error Message */}
        {error && (
          <div className="rounded-md bg-red-50 p-4 border border-red-200">
            <div className="flex">
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
              <p className="ml-3 text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Owner/Self-Edit Protection Banner */}
        {(isOwner || isSelf) && (
          <div className="rounded-md bg-amber-50 p-4 border border-amber-200">
            <div className="flex">
              <ShieldCheckIcon className="h-5 w-5 text-amber-600" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-amber-900">
                  {isOwner ? 'Company Owner' : 'Your Own Permissions'}
                </h3>
                <p className="mt-1 text-sm text-amber-700">
                  {isOwner
                    ? 'This employee is the company owner and has full access to all permissions. Permissions cannot be modified.'
                    : 'You cannot edit your own permissions. This is a security measure to prevent privilege escalation. Please contact another administrator.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Role Assignment */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Role Assignment</h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="roleId" className="block text-sm font-medium text-gray-700 mb-1">
                Employee Role <span className="text-red-500">*</span>
              </label>
              <select
                id="roleId"
                value={isCustomMode ? 'custom' : selectedRoleId}
                onChange={handleRoleChange}
                disabled={isOwner || isSelf || saving}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Select a role...</option>
                {availableRoles.map(role => (
                  <option key={role.id} value={role.id}>
                    {role.name} ({role.permissionCount} permissions)
                  </option>
                ))}
                <option value="custom">Custom</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {isCustomMode
                  ? 'Custom mode allows you to select specific permissions for this employee.'
                  : 'Changing the role will automatically update all permissions for this employee.'}
              </p>
            </div>

            {/* Current Role Badge */}
            {currentRoles.length > 0 && !isCustomMode && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Current role:</span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                  {currentRoles[0].name}
                </span>
                {hasPciAccess && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                    <ShieldCheckIcon className="h-3 w-3" />
                    PCI Access
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Custom Permission Selection UI */}
        {isCustomMode && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">
              Select Permissions
              <span className="ml-2 text-xs font-normal text-gray-500">
                ({selectedPermissions.length} selected)
              </span>
            </h3>

            {selectedPermissions.length === 0 && (
              <div className="mb-4 rounded-md bg-amber-50 p-4 border border-amber-200">
                <div className="flex">
                  <ExclamationCircleIcon className="h-5 w-5 text-amber-400" />
                  <p className="ml-3 text-sm text-amber-800">
                    Please select at least one permission for this custom role.
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {Object.keys(groupedPermissions)
                .sort((a, b) => {
                  const orderA = categoryConfig[a]?.order || 999;
                  const orderB = categoryConfig[b]?.order || 999;
                  return orderA - orderB;
                })
                .map(category => {
                  const categoryPerms = groupedPermissions[category] || [];
                  const config = categoryConfig[category] || { label: category, order: 999 };

                  // Check if all permissions in category are selected
                  const allCategorySelected = categoryPerms.every(p =>
                    selectedPermissions.includes(p.code)
                  );
                  const someCategorySelected = categoryPerms.some(p =>
                    selectedPermissions.includes(p.code)
                  );

                  return (
                    <div key={category} className="border border-gray-200 rounded-md overflow-hidden">
                      {/* Category Header with Select All */}
                      <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={allCategorySelected}
                              ref={el => {
                                if (el) el.indeterminate = someCategorySelected && !allCategorySelected;
                              }}
                              onChange={() => handleCategorySelectAll(category)}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <h4 className="text-sm font-medium text-gray-900">{config.label}</h4>
                          </label>
                          <span className="text-xs text-gray-500">
                            {categoryPerms.filter(p => selectedPermissions.includes(p.code)).length}/{categoryPerms.length}
                          </span>
                        </div>
                      </div>

                      {/* Permissions Checkboxes */}
                      <div className="bg-white">
                        {categoryPerms.map((permission, idx) => (
                          <div
                            key={permission.code}
                            className={`px-4 py-3 flex items-start gap-3 ${
                              idx !== categoryPerms.length - 1 ? 'border-b border-gray-100' : ''
                            }`}
                          >
                            <input
                              type="checkbox"
                              id={permission.code}
                              checked={selectedPermissions.includes(permission.code)}
                              onChange={() => handlePermissionToggle(permission.code)}
                              className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label
                              htmlFor={permission.code}
                              className="flex-1 min-w-0 cursor-pointer"
                            >
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-sm font-medium text-gray-900">
                                  {permission.name}
                                </p>
                                {permission.isPciSensitive && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200 flex-shrink-0">
                                    <ShieldCheckIcon className="h-3 w-3" />
                                    PCI
                                  </span>
                                )}
                              </div>
                              {permission.description && (
                                <p className="mt-0.5 text-xs text-gray-500">{permission.description}</p>
                              )}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Permissions Overview (for non-custom mode) */}
        {!isCustomMode && (
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-4">
              Permissions
              {currentPermissions.length > 0 && (
                <span className="ml-2 text-xs font-normal text-gray-500">
                  ({currentPermissions.length} total)
                </span>
              )}
            </h3>

            {currentPermissions.length === 0 ? (
              <div className="rounded-md bg-gray-50 p-6 text-center border border-gray-200">
                <ExclamationCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-600">
                  No permissions assigned. Please select a role to grant permissions.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {Object.keys(groupedPermissions)
                  .sort((a, b) => {
                    const orderA = categoryConfig[a]?.order || 999;
                    const orderB = categoryConfig[b]?.order || 999;
                    return orderA - orderB;
                  })
                  .map(category => {
                    // Filter permissions the employee currently has in this category
                    const categoryPerms = currentPermissions.filter(p => p.category === category);

                    if (categoryPerms.length === 0) return null;

                    const config = categoryConfig[category] || { label: category, order: 999 };

                    return (
                      <div key={category} className="border border-gray-200 rounded-md overflow-hidden">
                        {/* Category Header */}
                        <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-medium text-gray-900">{config.label}</h4>
                            <span className="text-xs text-gray-500">
                              {categoryPerms.length} permission{categoryPerms.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>

                        {/* Permissions List */}
                        <div className="bg-white">
                          {categoryPerms.map((permission, idx) => (
                            <div
                              key={permission.code}
                              className={`px-4 py-3 flex items-start gap-3 ${
                                idx !== categoryPerms.length - 1 ? 'border-b border-gray-100' : ''
                              }`}
                            >
                              <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <p className="text-sm font-medium text-gray-900">
                                    {permission.name}
                                  </p>
                                  {permission.isPciSensitive && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200 flex-shrink-0">
                                      <ShieldCheckIcon className="h-3 w-3" />
                                      PCI
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        )}

        {/* PCI Compliance Notice */}
        {hasPciAccess && (
          <div className="rounded-md bg-purple-50 p-4 border border-purple-200">
            <div className="flex">
              <ShieldCheckIcon className="h-5 w-5 text-purple-600" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-purple-900">PCI DSS Compliance</h3>
                <p className="mt-1 text-sm text-purple-700">
                  This employee has access to PCI-sensitive data. All access is logged for compliance purposes.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      {!isOwner && !isSelf && (
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || (!isCustomMode && !selectedRoleId) || (isCustomMode && selectedPermissions.length === 0)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </form>
  );
}

export default EmployeePermissionsTab;
